import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, MailCheck, Send } from 'lucide-react';
import { authApi } from '../api/client';

/**
 * Anonymous forgot-password page. On submit the outcome is always the same
 * neutral confirmation — the backend answers 202 whether or not the email has
 * an account, and this page must not hint otherwise.
 */
export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            await authApi.forgotPassword(email);
            setSent(true);
        } catch {
            setError('Could not send the reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl font-semibold text-slate-900">
                        Loanworker
                    </h1>
                    <p className="text-slate-600 mt-1">Reset your password</p>
                </div>

                {sent ? (
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                            <MailCheck size={16} className="mt-0.5 flex-shrink-0" />
                            <span>
                                If an account exists for {email}, a reset link has been
                                sent. The link expires in 1 hour.
                            </span>
                        </div>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4"
                    >
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <p className="text-sm text-slate-600">
                            Enter your account email and we'll send you a link to reset
                            your password.
                        </p>

                        <div>
                            <label
                                htmlFor="email"
                                className="block text-sm font-medium text-slate-700 mb-1"
                            >
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                disabled={isLoading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                            <Send size={16} />
                            {isLoading ? 'Sending...' : 'Send reset link'}
                        </button>
                    </form>
                )}

                <p className="text-center text-sm text-slate-600 mt-6">
                    Remembered it?{' '}
                    <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
